/**
 * IdentityChip — the category identity mark (UX-13 S0; CategoryChip
 * generalized from peace-hub-port/CategoryTiles into the shared layer).
 *
 * IdentityChip answers "which category is this entity"; Chip answers
 * "what does this fact mean." One is identity, the other is semantics —
 * do not reach for this to tag a fact, or for Chip to brand an entity.
 *
 * Data-driven and hex-free: pairs come from CATEGORY_TILE_COLORS (the
 * identity data file, the single source per the Phase 1 direction);
 * unknown categories fall back to the sand/sage pair. Sizes are the
 * ruled三: 34 (affiliation rows), 44 (cards), 58 (entity headers).
 * The radius keeps CategoryChip's proportional math (size × 0.29) so
 * every size renders the same squircle character.
 */

import { type ReactNode } from 'react';
import {
  Building2,
  type LucideIcon,
} from 'lucide-react';
import { CATEGORY_ICON } from '@/lib/categoryIcons';
import { CATEGORY_TILE_COLORS } from '@/lib/uiCategoryLabels';

export type IdentityChipSize = 34 | 44 | 58;

export interface IdentityChipProps {
  /** Category display name (the CATEGORY_TILE_COLORS key). */
  display: string;
  size?: IdentityChipSize;
  /** Icon stroke width; detail headers pass 1.7 for the refined chip. */
  stroke?: number;
}

export function IdentityChip({ display, size = 44, stroke }: IdentityChipProps): ReactNode {
  const Icon: LucideIcon = CATEGORY_ICON[display] ?? Building2;
  const colors = CATEGORY_TILE_COLORS[display] ?? {
    bg: 'var(--color-sand)',
    fg: 'var(--color-sage-600)',
  };
  const radius = Math.round(size * 0.29);
  return (
    <span
      data-testid="identity-chip"
      className="flex shrink-0 items-center justify-center"
      style={{ width: size, height: size, borderRadius: radius, background: colors.bg, color: colors.fg }}
    >
      <Icon size={Math.round(size * 0.48)} strokeWidth={stroke} aria-hidden="true" />
    </span>
  );
}
